import { cn } from '@invent/wl-ui-kit-next';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { NotPermitted, StatusChip, type ChipTone } from '@/design';
import { useCan } from '@/domains/auth';
import {
  CONTAINER_FIELD_TYPES,
  answerInstanceKey,
  domAddress,
  formatDateTime,
  gridRows,
  isCellsField,
  multiYearRows,
  surveyApi,
  tableLayout,
  type ISnapshotUsage
} from '@/domains/survey';
import { answerAddress, answerText, payloadText } from '../answer-text';
import { oversightApi } from '../api/oversight-api';
import type {
  IAnswerProvenance,
  IOversightAnswer,
  IOversightMetricNote,
  IOversightRepeat,
  IOversightResponseDetail,
  IOversightSection,
  IOversightUsage,
  ResponseStatus
} from '../api/oversight-contracts';
import { AnswerCorrection, type ICorrections } from '../components/answer-correction';
import { InternalFieldDisplay } from '../components/field-controls';
import { HistoryPopover, type IHistoryPage } from '../components/history-popover';
import { SectionNav } from '../components/section-nav';
import { recordSurveyPath } from '../components/participant-link';
import { HIDE_SYNC_LABEL, toHistoryRow } from '../components/timeline-values';
import { ORIGIN_WORDS, wordFor } from '../figures';
import { refusalMessage } from '../refusal';
import { useResponseContext, type IResponseContext } from './response-view-page';

/**
 * The response as its **pinned** questionnaire, read the way a questionnaire reads: its sections
 * down the left, and in each one question after question with its answer beside it — the stored
 * value, where it sits when it is one of several (a repeat row, a grid row, a year, an option's
 * sub), and where it came from as chips: its origin, and every source that has spoken about it.
 * It replaced a table per section, whose Field / Question / Answer / Origin columns made a
 * reviewer read four cells to learn one answer.
 *
 * Two things are on this screen because the API's detail carries them, and both matter to
 * a reviewer more than they look:
 *
 * - **Every usage is present, answered or not** — which makes "they skipped section 7"
 *   readable rather than inferable. Most of them are unanswered on a real response, so the
 *   list is filtered to answered ones by default and the switch shows the rest.
 * - **Orphans**: answers whose usage the pinned version no longer carries. They are the
 *   footprint of a re-published survey or a migration, and dropping them from the screen
 *   would make a value that exists in the database invisible to the only surface that
 *   looks at it.
 *
 * **It is still the oversight read, and nothing more.** There are no findings here and no render
 * state, and their absence is the contract: this surface never runs the rule engine — 49 453
 * migrated responses were never evaluated by it — so a "findings" card would either be empty or
 * be this app's own second opinion, and hiding what the engine would hide would be the same
 * second evaluator. The oversight channel for the same job is the review-flags pane. The
 * provenance chips say the stored `confirmationState` in its own word; the tone is this app's.
 *
 * **A correction is survey-api's write, judged there** (SR-222's F4). On a submitted or locked
 * response the tab also reads the response's definition snapshot — once, beside the oversight
 * read, never for a draft — because the oversight detail carries no options, columns or
 * constraints, and a correction draws the respondent's own control (the plan's R1). The snapshot
 * decides only *which control*; whether a value may be written is the server's matrix and finding
 * rule. Each answer carries `data-answer` and each question `data-usage` — the address in
 * `domAddress`'s spelling and the usage code — for the suites; the open correction's control
 * carries the runtime's own `data-address`, so no two elements share one. With "Show unanswered
 * fields" on, the addresses nobody answered are offered too (the plan's Q1, ruled (a)): a skipped
 * answer is what a correction fixes as often as a wrong one.
 *
 * **A question's history sits on the question, not on an answer** (the plan's R8): survey-api's
 * `timeline?usage=` keeps one usage's rows — every row of a grid, every repeat row — and filtering
 * one page of it by address here would filter a page, not the history. Each row says its address
 * instead, drawn as the Timeline tab draws it (`timeline-values.tsx`). A migrated response's
 * Salesforce rows come with it (SR-222's block 9), every roster slot's; the sync's are hidden by
 * the popover's switch, on at each opening, as on the Timeline tab.
 */
export function AdminResponseAnswersPage() {
  return <ResponseAnswers {...useResponseContext()} />;
}

/**
 * The questionnaire and its corrections, over one oversight read — the response view's Answers
 * tab, and a record page's survey tab (SR-431), which draws its latest response's here so an
 * answer written by a correction, a list's grid or an import reads the same on both: one written
 * value, one rendering. `showUnansweredFirst` is the switch's position as it opens — off on the
 * response, on on a survey tab (SR-427: a blank answer is to be seen there); `level` the heading
 * level of each questionnaire section, 3 under a record page's own section; `anchorPrefix` keeps
 * the sections' anchors apart from the record page's own.
 */
export function ResponseAnswers({
  response,
  etag,
  reload,
  adopt,
  showUnansweredFirst = false,
  level = 2,
  anchorPrefix = 'answers'
}: IResponseContext & {
  showUnansweredFirst?: boolean;
  level?: 2 | 3;
  anchorPrefix?: string;
}) {
  const [showUnanswered, setShowUnanswered] = React.useState(showUnansweredFirst);
  const metricNotes = React.useMemo<IMetricNotes>(
    () => ({
      notes: response.metrics ?? [],
      figures: recordSurveyPath(response.participant.participantId, response.survey.code)
    }),
    [response.metrics, response.participant.participantId, response.survey.code]
  );
  const snapshot = useCorrectionSnapshot(response, etag);
  // A correction is `responses.annotate` (SR-228): without it the pencils are drawn off under one
  // sentence, the snapshot still read — which answers are correctable is the same either way.
  const permitted = useCan()('responses.annotate');
  const reasonId = React.useId();

  // What the tab keeps across corrections: the last reason per address (R6), and what an answer
  // was told by a correction whose re-read may take the correction itself away (R7).
  const reasons = React.useRef(new Map<string, string>()).current;
  const [told, setTold] = React.useState<ReadonlyMap<string, string>>(() => new Map());
  const tell = React.useCallback((address: string, sentence: Nullable<string>) => {
    setTold((previous) => {
      const next = new Map(previous);
      if (sentence === null) next.delete(address);
      else next.set(address, sentence);
      return next;
    });
  }, []);
  const corrections = React.useMemo<Nullable<ICorrections>>(
    () =>
      etag === null
        ? null
        : {
            responseId: response.responseId,
            etag,
            participantName: response.participant.displayName,
            adopt,
            reread: reload,
            reasons,
            tell,
            notPermitted: permitted ? undefined : reasonId
          },
    [
      response.responseId,
      response.participant.displayName,
      etag,
      adopt,
      reload,
      reasons,
      tell,
      permitted,
      reasonId
    ]
  );

  const answeredCount = React.useMemo(
    () =>
      response.sections.reduce(
        (total, section) =>
          total + section.usages.filter((usage) => usage.answers.length > 0).length,
        0
      ),
    [response]
  );

  // A question's history, read afresh on each opening of its popover (`HistoryPopover`).
  const history = React.useCallback(
    async (usage: string, page: number, signal: AbortSignal): Promise<IHistoryPage> => {
      const read = await oversightApi.timeline(response.responseId, { usage, page }, signal);
      return { ...read, items: read.items.map(toHistoryRow) };
    },
    [response.responseId]
  );

  // A question a correction was told something about stays drawn with the switch off: a re-read
  // that finds its answer cleared would otherwise take the sentence — and the draft — away.
  const toldUsages = new Set([...told.keys()].map((key) => parseAddress(key).usage));
  const sections = response.sections
    .map((section, index) => ({
      section,
      anchor: `${anchorPrefix}-${index}`,
      usages: section.usages.filter(
        (usage) => showUnanswered || usage.answers.length > 0 || toldUsages.has(usage.code)
      )
    }))
    .filter((entry) => entry.usages.length > 0);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-muted-foreground">
          {answeredCount} answered {answeredCount === 1 ? 'field' : 'fields'}
          {response.repeats.length > 0 && ` · ${response.repeats.length} repeat rows`}
        </p>
        <label className="flex items-center gap-2">
          <Switch checked={showUnanswered} onCheckedChange={setShowUnanswered} />
          Show unanswered fields
        </label>
      </div>

      <CorrectionsNotice snapshot={snapshot} />
      {!permitted && corrections && snapshot.state === 'loaded' && (
        <NotPermitted id={reasonId} what="correcting a submitted answer" />
      )}

      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)]">
        <SectionNav
          variant="column"
          label="Sections"
          entries={sections.map(({ section, anchor }) => ({ anchor, label: sectionTitle(section) }))}
        />
        <div className="grid min-w-0 gap-6">
          {sections.length === 0 && (
            <p className="text-muted-foreground">
              Nothing is answered on this response. The switch above shows every question.
            </p>
          )}
          <MetricNotesContext.Provider value={metricNotes}>
            {sections.map(({ section, anchor, usages }) => (
              <QuestionnaireSection
                key={anchor}
                anchor={anchor}
                title={sectionTitle(section)}
                level={level}
                usages={usages}
                snapshot={snapshot}
                repeats={response.repeats}
                corrections={corrections}
                told={told}
                showUnanswered={showUnanswered}
                participantName={response.participant.displayName}
                history={history}
              />
            ))}
          </MetricNotesContext.Provider>

          {response.orphans.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Answers outside this version</CardTitle>
                <CardDescription>
                  Stored against usages the pinned version does not carry — what a re-publish or
                  a migration leaves behind. Addressed by usage id, because there is no code to
                  show: the usage row itself is not in this version.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-72">Usage id</TableHead>
                      <TableHead>Answer</TableHead>
                      <TableHead className="w-32">Origin</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {response.orphans.map((orphan) => (
                      <TableRow
                        key={`${orphan.usageId}-${orphan.instanceId}-${orphan.instanceKey}`}
                      >
                        <TableCell className="font-mono text-xs break-all">
                          {orphan.usageId}
                        </TableCell>
                        <TableCell>{payloadText(orphan.value)}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {orphan.origin}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  );
}

function sectionTitle(section: IOversightSection): string {
  return section.code ? `${section.code} · ${section.title}` : 'Fields with no section';
}

/** The statuses survey-api corrects — the others answer 409 `response-not-writable`. */
const CORRECTABLE_STATUSES: ReadonlySet<ResponseStatus> = new Set(['submitted', 'locked']);

/**
 * Whether this tab can offer corrections, and the snapshot they are drawn over (R1, R5).
 *
 * - `off`: a status survey-api does not correct (nothing said — a draft is the respondent's — unless
 *   the status moved there under this tab, which closed the corrections open on it), or
 *   no `ETag` came with the read (said: no correction is ever sent without `If-Match`);
 * - `reading`: the snapshot is out — no pencil yet;
 * - `failed`: said once, with the read's words, and the tab stays read-only;
 * - `loaded`: the snapshot's usages by code.
 */
type CorrectionSnapshot =
  | { state: 'off'; reason: Nullable<string> }
  | { state: 'reading' }
  | { state: 'failed'; message: string }
  | { state: 'loaded'; usages: ReadonlyMap<string, ISnapshotUsage> };

/**
 * The definition snapshot, read once per tab of a correctable response. Keyed on the response and
 * on whether it is correctable, so a correction's adopted detail — same response, same status —
 * reads nothing again, and a re-read that finds the response no longer correctable drops it.
 */
function useCorrectionSnapshot(
  response: IOversightResponseDetail,
  etag: Nullable<string>
): CorrectionSnapshot {
  const open = CORRECTABLE_STATUSES.has(response.status);
  const wanted = open && etag !== null;
  // Whether this tab has offered corrections: a status that moves under it is then said, since
  // the corrections open on it closed with it.
  const everOpen = React.useRef(false);
  if (open) everOpen.current = true;
  const [read, setRead] = React.useState<CorrectionSnapshot>({ state: 'reading' });

  React.useEffect(() => {
    if (!wanted) return;
    const controller = new AbortController();
    setRead({ state: 'reading' });
    surveyApi.getDefinition(response.responseId, controller.signal).then(
      (definition) => {
        if (controller.signal.aborted) return;
        setRead(
          definition
            ? {
                state: 'loaded',
                usages: new Map(definition.usages.map((usage) => [usage.code, usage]))
              }
            : { state: 'failed', message: 'No questionnaire was found for this response.' }
        );
      },
      (error: unknown) => {
        if (controller.signal.aborted) return;
        setRead({ state: 'failed', message: refusalMessage(error, 'The read failed.') });
      }
    );
    return () => controller.abort();
  }, [response.responseId, wanted]);

  if (!open) {
    return {
      state: 'off',
      reason: everOpen.current
        ? `This response is ${response.status} now — corrections are not offered.`
        : null
    };
  }
  if (etag === null) {
    return {
      state: 'off',
      reason: 'Corrections are not available: this response came without its revision.'
    };
  }
  return read;
}

/** Why the tab is read-only on a response survey-api would correct — said once, above it. */
function CorrectionsNotice({ snapshot }: { snapshot: CorrectionSnapshot }) {
  let said: Nullable<string> = null;
  if (snapshot.state === 'failed') {
    said =
      'Corrections are not available: the questionnaire could not be read — ' +
      snapshot.message;
  } else if (snapshot.state === 'off') {
    said = snapshot.reason;
  }
  return said ? <p className="text-sm text-muted-foreground">{said}</p> : null;
}

/**
 * Whether one stored answer is offered a correction (R3), once the snapshot is loaded on a
 * submitted or locked response: its usage is in the snapshot — an orphan's is not, and it is never
 * offered — and is neither computed nor a container (no answer of its own); and an answer inside a
 * repeat row sits in a row the detail lists as `active`. A removed or excluded row is not
 * corrected, and a row the detail does not list is not offered either, since survey-api would
 * answer it `unknown_instance`. Nor is a table row holding no cell anybody writes — a heading row,
 * or one every cell of which is computed or N/A (FA-PM-004's opening and closing balances, SR-416):
 * survey-api refuses every correction of it `cell_read_only`, so its value is drawn with no pencil
 * (`writableRow`). Nothing else is judged here: every other refusal is survey-api's.
 */
function correctable(
  answer: IOversightAnswer,
  usage: IOversightUsage,
  snapshotUsage: ISnapshotUsage | undefined,
  repeats: readonly IOversightRepeat[]
): boolean {
  if (!snapshotUsage || usage.isComputed || snapshotUsage.isComputed) return false;
  if (CONTAINER_FIELD_TYPES.has(snapshotUsage.fieldType)) return false;
  if (!writableRow(snapshotUsage, answer.instanceKey)) return false;
  if (answer.instanceId === null) return true;
  return repeats.some((row) => row.instanceId === answer.instanceId && row.status === 'active');
}

/**
 * The addresses a question can be answered at, as the snapshot names them **without a rule** (the
 * plan's Q1, ruled (a)) — so an address nobody answered can be answered by a correction: a scalar
 * at `answerInstanceKey`, a grid's every row and a year table's every year, each in every
 * **active** row of the repeat the usage sits in, or once outside one. A computed usage and a
 * container have none. Whether an option's sub is triggered, or an answer would raise a finding,
 * is not asked here: survey-api refuses what does not fit (`trigger_key`, `unknown_instance`,
 * `constraint`), and judging it here would be the second evaluator.
 */
function addressesOf(
  usage: ISnapshotUsage,
  snapshot: ReadonlyMap<string, ISnapshotUsage>,
  repeats: readonly IOversightRepeat[]
): { instanceId: Nullable<string>; instanceKey: string }[] {
  if (usage.isComputed || CONTAINER_FIELD_TYPES.has(usage.fieldType)) return [];
  let keys: string[];
  if (isCellsField(usage.fieldType)) {
    const rows = usage.fieldType === 'grid' ? gridRows(usage) : multiYearRows(usage, undefined);
    keys = rows.map((row) => row.key).filter((key) => writableRow(usage, key));
  } else {
    keys = [answerInstanceKey(usage)];
  }
  const repeat = enclosingRepeat(usage, snapshot);
  const instanceIds =
    repeat === null
      ? [null]
      : repeats
          .filter((row) => row.usage === repeat && row.status === 'active')
          .map((row) => row.instanceId);
  return instanceIds.flatMap((instanceId) =>
    keys.map((instanceKey) => ({ instanceId, instanceKey }))
  );
}

/** Whether a correction can write anything at this address: a table row holding at least one cell
 *  the respondent writes (`tableLayout`, survey-api's own expansion); any other usage, always. */
function writableRow(usage: ISnapshotUsage, instanceKey: string): boolean {
  return !isCellsField(usage.fieldType) || tableLayout(usage).writableColumns(instanceKey).length > 0;
}

/** The nearest `repeat_group` the usage sits under, by its code, or `null` outside one. */
function enclosingRepeat(
  usage: ISnapshotUsage,
  snapshot: ReadonlyMap<string, ISnapshotUsage>
): Nullable<string> {
  const seen = new Set<string>();
  for (let parent = usage.parentUsage; parent !== null && !seen.has(parent); ) {
    seen.add(parent);
    const container = snapshot.get(parent);
    if (!container) return null;
    if (container.fieldType === 'repeat_group') return container.code;
    parent = container.parentUsage;
  }
  return null;
}

/**
 * One place an answer sits on the tab: a stored answer at its address, or — with "Show unanswered
 * fields" on and corrections open — an address nobody answered (`stored` null, Q1).
 */
interface IAnswerSlot {
  instanceId: Nullable<string>;
  instanceKey: string;
  stored: Nullable<IOversightAnswer>;
  /** Whether an address nobody answered is offered a correction — `false` for one drawn only for
   *  what it was told, at an address the snapshot no longer offers (a repeat row gone). */
  offered?: boolean;
}

/** `domAddress`'s spelling read back — the told map's keys. */
function parseAddress(key: string): {
  usage: string;
  instanceId: Nullable<string>;
  instanceKey: string;
} {
  const [usage, instanceId, ...rest] = key.split('|');
  return { usage, instanceId: instanceId || null, instanceKey: rest.join('|') };
}

/** Whether two places of one question are the same address — its row and its key. */
function sameAddress(
  a: Pick<IAnswerSlot, 'instanceId' | 'instanceKey'>,
  b: Pick<IAnswerSlot, 'instanceId' | 'instanceKey'>
): boolean {
  return a.instanceId === b.instanceId && a.instanceKey === b.instanceKey;
}

/** The question's history hides the sync's Salesforce rows, as the Timeline tab does. */
const HIDE_SYNC = { label: HIDE_SYNC_LABEL };

/** A question's history read, by its usage code — one function for the whole tab. */
type QuestionHistory = (usage: string, page: number, signal: AbortSignal) => Promise<IHistoryPage>;

/** One section: its title, then question and answer per line. */
function QuestionnaireSection({
  anchor,
  title,
  level,
  usages,
  snapshot,
  repeats,
  corrections,
  told,
  showUnanswered,
  participantName,
  history
}: {
  anchor: string;
  title: string;
  level: 2 | 3;
  usages: IOversightUsage[];
  snapshot: CorrectionSnapshot;
  repeats: readonly IOversightRepeat[];
  corrections: Nullable<ICorrections>;
  told: ReadonlyMap<string, string>;
  showUnanswered: boolean;
  participantName: string;
  history: QuestionHistory;
}) {
  const id = React.useId();
  const Heading = level === 2 ? 'h2' : 'h3';
  return (
    <section
      id={anchor}
      aria-labelledby={id}
      className="scroll-mt-4 rounded-lg border border-border bg-card lg:scroll-mt-[calc(4rem+var(--section-bar-h,0px))]"
    >
      <Heading
        id={id}
        className="border-b border-border px-4 py-2.5 text-[0.9375rem] font-medium text-[var(--color-royal-blue)]"
      >
        {title}
      </Heading>
      <ol className="divide-y divide-border">
        {usages.map((usage) => (
          <Question
            key={usage.code}
            usage={usage}
            snapshot={snapshot}
            repeats={repeats}
            corrections={corrections}
            told={told}
            showUnanswered={showUnanswered}
            participantName={participantName}
            history={history}
          />
        ))}
      </ol>
    </section>
  );
}

/**
 * One question and what it holds. A usage holding several answers — a repeat member, a grid row,
 * a per-option sub — lists each at its address: flattening them into one value would hide where
 * each answer sits, which is what a reviewer chasing a value in the database needs. With the
 * switch on and corrections open, the addresses nobody answered follow the stored ones, each
 * offered a correction (Q1).
 */
function Question({
  usage,
  snapshot,
  repeats,
  corrections,
  told,
  showUnanswered,
  participantName,
  history
}: {
  usage: IOversightUsage;
  snapshot: CorrectionSnapshot;
  repeats: readonly IOversightRepeat[];
  corrections: Nullable<ICorrections>;
  told: ReadonlyMap<string, string>;
  showUnanswered: boolean;
  participantName: string;
  history: QuestionHistory;
}) {
  const snapshotUsages = snapshot.state === 'loaded' ? snapshot.usages : null;
  const snapshotUsage = snapshotUsages?.get(usage.code);
  // The addresses a correction can answer here, as the snapshot names them — none before the
  // snapshot is loaded, or on a computed usage.
  const addresses = () =>
    snapshotUsages && snapshotUsage && !usage.isComputed
      ? addressesOf(snapshotUsage, snapshotUsages, repeats)
      : [];
  const slots: IAnswerSlot[] = usage.answers.map((answer) => ({
    instanceId: answer.instanceId,
    instanceKey: answer.instanceKey,
    stored: answer
  }));
  if (showUnanswered && corrections) {
    for (const address of addresses()) {
      const answered = usage.answers.some((answer) => sameAddress(answer, address));
      if (!answered) slots.push({ ...address, stored: null });
    }
  }
  // An address told something by a correction, whose answer a re-read took away: drawn, so the
  // sentence has a place — and offered again only where the snapshot still names the address.
  for (const key of told.keys()) {
    const address = parseAddress(key);
    if (address.usage !== usage.code) continue;
    if (slots.some((slot) => sameAddress(slot, address))) continue;
    const offered = addresses().some((candidate) => sameAddress(candidate, address));
    slots.push({
      instanceId: address.instanceId,
      instanceKey: address.instanceKey,
      stored: null,
      offered
    });
  }
  return (
    <li
      data-usage={usage.code}
      className="grid gap-x-6 gap-y-2 px-4 py-3 md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]"
    >
      <div className="min-w-0">
        <p className="font-mono text-[0.6875rem] text-muted-foreground">{usage.code}</p>
        <div className="flex items-start gap-1">
          <p className="min-w-0 break-words">{usage.label}</p>
          <HistoryPopover
            label={usage.label}
            name={participantName}
            read={(page, signal) => history(usage.code, page, signal)}
            emptyText="No change recorded for this question."
            hide={HIDE_SYNC}
          />
        </div>
        {usage.isComputed && (
          <span className="mt-1 inline-block">
            <StatusChip>computed</StatusChip>
          </span>
        )}
      </div>
      <div className="grid min-w-0 gap-2.5">
        {slots.length === 0 ? (
          <p className="text-muted-foreground">
            not answered{' '}
            <span className="font-mono text-[0.6875rem]">· {usage.fieldType}</span>
          </p>
        ) : (
          slots.map((slot) => {
            const domKey = domAddress(usage.code, slot.instanceId, slot.instanceKey);
            const offered =
              slot.stored === null
                ? slot.offered !== false
                : correctable(slot.stored, usage, snapshotUsage, repeats);
            return (
              <Answer
                key={`${slot.instanceId}-${slot.instanceKey}`}
                domKey={domKey}
                usage={usage.code}
                slot={slot}
                label={usage.label}
                correction={
                  corrections && snapshotUsage && offered ? { corrections, snapshotUsage } : null
                }
                told={told.get(domKey)}
              />
            );
          })
        )}
      </div>
    </li>
  );
}

/**
 * The value, where it sits, and the chips for where it came from. Provenance is a list rather
 * than a label because one answer can carry several — one per source that has spoken about it —
 * and a `proposed` source value beside an overridden one is exactly the disagreement a reviewer
 * opened this screen for, so each source's own value is said beside its chip.
 *
 * `domKey` is the answer's `data-answer`. `correction` is drawn where R3's `correctable` said so,
 * or on an address nobody answered (Q1) — the value then read and corrected in place
 * (`AnswerCorrection`); `told` is what a correction was last told here, said when the correction
 * is no longer drawn (a status that moved under it).
 */
function Answer({
  domKey,
  usage,
  slot,
  label,
  correction,
  told
}: {
  domKey: string;
  /** The usage's code, for its metric notes. */
  usage: string;
  slot: IAnswerSlot;
  label: string;
  correction: Nullable<{ corrections: ICorrections; snapshotUsage: ISnapshotUsage }>;
  told: string | undefined;
}) {
  const answer = slot.stored;
  const address = answerAddress(slot);
  const value = answer ? (
    <span
      className={cn(
        'font-medium break-words',
        answer.isExcluded && 'text-muted-foreground line-through'
      )}
    >
      {answerText(answer)}
    </span>
  ) : (
    <span className="text-muted-foreground">not answered</span>
  );
  return (
    <div className="grid gap-1" data-answer={domKey}>
      {address && <span className="text-[0.6875rem] text-muted-foreground">{address}</span>}
      {correction ? (
        <AnswerCorrection
          label={address ? `${label} · ${address}` : label}
          snapshotUsage={correction.snapshotUsage}
          instanceId={slot.instanceId}
          instanceKey={slot.instanceKey}
          stored={answer}
          display={value}
          corrections={correction.corrections}
        />
      ) : (
        <>
          {value}
          {told && (
            <p role="alert" className="text-sm text-destructive">
              {told}
            </p>
          )}
        </>
      )}
      {slot.instanceId === null && (
        <MetricNotes usage={usage} instanceKey={slot.instanceKey} />
      )}
      {answer && (
        <span className="flex flex-wrap items-center gap-1.5">
          <StatusChip>{answer.origin}</StatusChip>
          {answer.isExcluded && <StatusChip tone="attention">excluded</StatusChip>}
          {answer.provenance.map((source) => (
            <ProvenanceChip key={`${source.sourceSystem}-${source.asOf}`} source={source} />
          ))}
        </span>
      )}
    </div>
  );
}

interface IMetricNotes {
  notes: readonly IOversightMetricNote[];
  /** The record's survey tab, where Asset & production holds the metrics. */
  figures: string;
}

/** The response's metric notes, handed to every answer without threading them through the
 *  sections and questions. */
const MetricNotesContext = React.createContext<IMetricNotes>({ notes: [], figures: '' });

/**
 * Where a metric made from this answer now holds another value (SR-451; Дмитрий's ruling on
 * RNK-09 AC4): a firm's file or staff writes the metric and never the advisor's answer, so the
 * answer read here is not what a ranking reads — said under it, one line per metric, with what
 * the metric holds, where it came from and when. survey-api decides which answers differ; this
 * side draws its notes as sent.
 */
function MetricNotes({ usage, instanceKey }: { usage: string; instanceKey: string }) {
  const { notes, figures } = React.useContext(MetricNotesContext);
  const mine = notes.filter((note) => note.usage === usage && note.instanceKey === instanceKey);
  if (mine.length === 0) return null;
  return (
    <ul className="grid gap-0.5 text-xs">
      {mine.map((note) => (
        <li key={`${note.metric}-${note.asOf}`} data-metric-note={note.metric}>
          <StatusChip tone="info">metric now</StatusChip>{' '}
          {note.column && <span className="font-mono text-muted-foreground">{note.column}: </span>}
          {note.value === null ? (
            'cleared'
          ) : (
            <InternalFieldDisplay payload={{ t: 'money', v: note.value }} />
          )}{' '}
          <span className="text-muted-foreground">
            — {wordFor(ORIGIN_WORDS, note.source)}, {formatDateTime(note.recordedAt)} ·{' '}
            <Link className="underline" to={figures}>
              Asset &amp; production
            </Link>
          </span>
        </li>
      ))}
    </ul>
  );
}

function ProvenanceChip({ source }: { source: IAnswerProvenance }) {
  return (
    <span className="inline-flex max-w-full flex-wrap items-center gap-1 text-xs text-muted-foreground">
      <StatusChip tone={CONFIRMATION_TONE[source.confirmationState] ?? 'neutral'}>
        {source.sourceSystem} · {source.confirmationState}
      </StatusChip>
      <span className="break-words">{payloadText(source.sourceValue)}</span>
      {source.reviewFlagged && <StatusChip tone="attention">flagged</StatusChip>}
    </span>
  );
}

/** A source's standing, in survey-api's words: a proposal is still waiting on the respondent, a
 *  confirmation settled, an override the respondent's own; anything else a plain state. */
const CONFIRMATION_TONE: Record<string, ChipTone> = {
  proposed: 'attention',
  confirmed: 'success',
  overridden: 'info'
};
